import { EnumChip } from '@/common/card/EnumChip';
import { useContextMenu } from '@/common/contextMenu/useContextMenu';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityPropForm } from '@/common/entities/entityProp/EntityPropForm';
import { EntityPropTable } from '@/common/entities/entityProp/EntityPropTable';
import { buildPropIdFilter } from '@/common/entities/entityProp/propIdFilterUtils';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { EntityCardList } from '@/common/entityFramework/entityCardList/EntityCardList';
import { EntityFormDialog } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useEnumDisplayNames } from '@/common/hooks/useEnumDisplayNames';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ProgContextStrip } from '../prog/ProgContextStrip';
import { formatFormulaDisplay } from '../tier/formFields/TierFormulaUtils';
import { useTierFormulaPalette } from '../tier/formFields/useTierFormulaPalette';
import { ProgProdTierForm } from '../tier/ProgProdTierForm';
import { TierBulkDialog } from '../tier/TierBulkDialog';
import { ProgProdBulkDialog } from './ProgProdBulkDialog';
import { PCT_CALC_TYPES } from './progProdCalcTypeUtils';
import { ProgProdForm } from './ProgProdForm';

const formatValue = (calcType, value) => {
  if (value == null || value === '') return '—';
  if (PCT_CALC_TYPES.includes(calcType)) {
    const num = Number(value);
    return Number.isFinite(num) ? `${Math.round(num * 10000) / 100}%` : value;
  }
  return value;
};

// One expandable card per Program Product; the card body holds that product's Tiers (when tier-based) and Properties.
// All add/edit happens in dialogs so the Program workspace never navigates away.
// showPricing=false hides calc type / basis / value on the card (e.g. tier-based programs where pricing lives on the tiers)
export const ProgProdCardList = ({ progObj, canEdit, basisLabel = 'Basis', showPricing = true }) => {
  const rsrc = useRsrc();
  const { openDialog } = useDialog();
  const { hasRole } = usePrincipal();
  const { navigateTo } = useNavState();
  const { openContextMenu, contextMenu: tierContextMenu } = useContextMenu();

  const [highlightId, setHighlightId] = useState(null);
  const [progProdIdToTiers, setProgProdIdToTiers] = useState({});

  const canSaveProgProd = canEdit && hasRole('MD_PROG_PROD_SAVE');
  const canDeleteProgProd = canEdit && hasRole('MD_PROG_PROD_DELETE');
  const canDeleteTier = canEdit && hasRole('MD_PROG_PROD_TIER_DELETE');
  const canSaveProp = canEdit && hasRole('MD_ENTITY_PROP_SAVE');

  const { getEnumSpec: progProdEnumSpec } = useEnumDisplayNames('md', 'PROG_PROD');
  const { getEnumSpec: tierEnumSpec } = useEnumDisplayNames('md', 'PROG_PROD_TIER');

  // Used only to render formulas (variable display names, value formatting) in the read-only summary table
  const { codeToVariable } = useTierFormulaPalette(progObj.id);

  const augRowsReq = useCallback(
    (rowsReq) => {
      rowsReq.expr = ExprUtils.whereParams('progId', progObj.id);
    },
    [progObj.id],
  );

  // Tiers for every product in this Program are loaded once and grouped per owner
  const { refreshTrigger: tierRefreshTrigger } = useEntityChangeSubscr('md', 'PROG_PROD_TIER');
  useEffect(() => {
    if (!progObj.id) return;
    rsrc
      .rows('md', 'PROG_PROD_TIER', {
        query: 'TIER_LIST',
        expr: ExprUtils.whereParams('progId', progObj.id),
      })
      .then((rows) => {
        const grouped = {};
        (rows ?? []).forEach((tier) => {
          (grouped[tier.progProdId] ??= []).push(tier);
        });
        Object.values(grouped).forEach((tiers) => tiers.sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0)));
        setProgProdIdToTiers(grouped);
      });
  }, [progObj.id, progObj.ctrtId, tierRefreshTrigger]);

  const openProgProdDialog = (mode, progProd) => {
    const initialValues =
      mode === EntityFormMode.ADD ?
        {
          progId: progObj.id,
          progCode: progObj.code,
          progName: progObj.name,
          ctrtId: progObj.ctrtId,
          startDate: progObj.startDate,
          endDate: progObj.endDate,
        }
      : undefined;

    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add Products' : 'Edit Product'} — ${progObj.code}`,
      module: 'md',
      entity: 'PROG_PROD',
      mode,
      entityId: progProd?.id,
      initialValues,
      allowAddAnother: mode === EntityFormMode.ADD,
      maxWidth: 'xl',
      header: <ProgContextStrip progObj={progObj} />,
      renderForm: (formProps) => <ProgProdForm progObj={progObj} {...formProps} />,
    }).then((saved) => {
      if (!saved) return;
      const last = Array.isArray(saved) ? saved[saved.length - 1] : saved;
      setHighlightId(last?.id ?? null);
    });
  };

  const openTierDialog = (mode, progProd, tier) => {
    const existing = progProdIdToTiers[progProd.id] ?? [];
    // Tiers are numbered from 0
    const nextTier = existing.length === 0 ? 0 : existing.reduce((max, t) => Math.max(max, t.tier ?? 0), 0) + 1;
    const initialValues =
      mode === EntityFormMode.ADD ?
        {
          progProdId: progProd.id,
          ctrtId: progObj.ctrtId,
          progId: progObj.id,
          tier: nextTier,
          progProdCalcType: progProd.progProdCalcType,
          pymtBasisId: progProd.pymtBasisId,
          // Tier 0 is the baseline/access Tier - always [ACCESS], with disc defaulted to 0 but still editable
          ...(nextTier === 0 ? { formula: '[ACCESS]', conc: 0 } : {}),
        }
      : undefined;

    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add' : 'Edit'} Tier — ${progProd.code}`,
      module: 'md',
      entity: 'PROG_PROD_TIER',
      mode,
      entityId: tier?.id,
      initialValues,
      allowAddAnother: mode === EntityFormMode.ADD,
      getNextInitialValues: (savedTier, values) => {
        const { formula, ...rest } = values;
        return { ...rest, tier: (savedTier.tier ?? values.tier) + 1 };
      },
      renderForm: (formProps) => <ProgProdTierForm ownerObj={progProd} {...formProps} />,
      maxWidth: 'xl',
    }).then((saved) => {
      if (saved) setHighlightId(progProd.id);
    });
  };

  // Same right-click pattern as grid rows: Details, then Delete when permitted
  const contextMenuItems = (entity, row, onDelete) => {
    const items = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () =>
          navigateTo(`md/${entity === 'PROG_PROD' ? 'prog-prod' : 'prog-prod-tier'}`, {
            mode: Mode.DETAIL,
            entityId: row.id,
          }),
      },
    ];
    if (onDelete) {
      items.push({
        name: 'Delete',
        icon: fas.faTrash,
        disabled: row.status === 'APPROVED' || row.status === 'APPROVING' || row.status === 'IMPL',
        action: () => openDialog(ConfirmDeleteDialog, { data: { module: 'md', entity, id: row.id } }),
      });
    }
    return items;
  };

  const renderSummary = (progProd) => {
    const tiers = progProdIdToTiers[progProd.id] ?? [];
    return (
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Typography fontWeight={600} noWrap>
            {progProd.prodCode}
            {progProd.prodName ? ` · ${progProd.prodName}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {progProd.code}
          </Typography>
        </Box>
        <Summary
          label="Calc"
          value={
            <EnumChip
              enumSpec={progProdEnumSpec('progProdCalcType', progProd.progProdCalcType)}
              code={progProd.progProdCalcType}
            />
          }
          raw
          hidden={!showPricing}
        />
        <Summary
          label={basisLabel}
          value={
            <EnumChip enumSpec={progProdEnumSpec('pymtBasisId', progProd.pymtBasisId)} code={progProd.pymtBasisCode} />
          }
          raw
          hidden={!showPricing}
        />
        <Summary
          label="Concession"
          value={formatValue(progProd.progProdCalcType, progProd.conc)}
          hidden={!showPricing}
        />
        <Summary
          label="Dates"
          value={`${DateUtils.format(progProd.startDate)} – ${DateUtils.format(progProd.endDate)}`}
        />
        {progObj.tierBased && (
          <Chip size="small" variant="outlined" label={`${tiers.length} tier${tiers.length === 1 ? '' : 's'}`} />
        )}
        {progProd.valStatus && progProd.valStatus !== 'VALID' && progProd.valStatus !== 'NONE' && (
          <Chip size="small" color="warning" label={progProd.valStatus} />
        )}
        <Stack direction="row" sx={{ ml: 'auto' }} onClick={(event) => event.stopPropagation()}>
          {canSaveProgProd && (
            <Tooltip title="Edit product">
              <IconButton size="small" onClick={() => openProgProdDialog(EntityFormMode.EDIT, progProd)}>
                <FontAwesomeIcon icon={fas.faPencil} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderBody = (progProd) => {
    const tiers = progProdIdToTiers[progProd.id] ?? [];
    return (
      <Stack spacing={2}>
        {progObj.tierBased && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Tiers</Typography>
            {tiers.length === 0 ?
              <Typography variant="body2" color="text.secondary">
                No tiers yet.
              </Typography>
            : <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Formula</TableCell>
                    <TableCell>Calc</TableCell>
                    <TableCell>{basisLabel}</TableCell>
                    <TableCell align="right">Concession</TableCell>
                    {canEdit && <TableCell align="right" sx={{ width: 56 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow
                      key={tier.id}
                      hover
                      sx={canEdit ? { cursor: 'pointer' } : undefined}
                      onClick={canEdit ? () => openTierDialog(EntityFormMode.EDIT, progProd, tier) : undefined}
                      onContextMenu={(event) =>
                        openContextMenu(event, contextMenuItems('PROG_PROD_TIER', tier, canDeleteTier))
                      }
                    >
                      <TableCell>{tier.tier}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {formatFormulaDisplay(tier.formula, codeToVariable)}
                      </TableCell>
                      <TableCell>
                        <EnumChip
                          enumSpec={tierEnumSpec('progProdCalcType', tier.progProdCalcType)}
                          code={tier.progProdCalcType}
                        />
                      </TableCell>
                      <TableCell>
                        <EnumChip enumSpec={tierEnumSpec('pymtBasisId', tier.pymtBasisId)} code={tier.pymtBasisCode} />
                      </TableCell>
                      <TableCell align="right">{formatValue(tier.progProdCalcType, tier.conc)}</TableCell>
                      {canEdit && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit tier">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openTierDialog(EntityFormMode.EDIT, progProd, tier);
                              }}
                            >
                              <FontAwesomeIcon icon={fas.faPencil} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            }
          </Stack>
        )}
        <EntityPropTable
          parentEntity="PROG_PROD"
          parentEntityObj={progProd}
          canEdit={canEdit}
          typeFilterValues={[progProd.progProdCalcType, progObj.progType]}
          showAddButton={false}
        />
      </Stack>
    );
  };

  // Lets the Property being added apply to the Product itself or to any of its existing Tiers
  const openProgProdPropDialog = (progProd) => {
    const tiers = progProdIdToTiers[progProd.id] ?? [];
    const ownerOptions = [
      { label: 'This Product', entity: 'PROG_PROD', entityId: progProd.id, ownerObj: progProd },
      ...tiers.map((tier) => ({
        label: `Tier ${tier.tier}`,
        entity: 'PROG_PROD_TIER',
        entityId: tier.id,
        ownerObj: tier,
      })),
    ];
    const propIdFilter = buildPropIdFilter([progProd.progProdCalcType, progObj.progType]);

    openDialog(EntityFormDialog, {
      title: `Add Property — ${progProd.code}`,
      module: 'md',
      entity: 'ENTITY_PROP',
      mode: EntityFormMode.ADD,
      initialValues: { entity: 'PROG_PROD', entityId: progProd.id },
      maxWidth: 'xl',
      renderForm: (formProps) => (
        <EntityPropForm ownerObj={progProd} propIdFilter={propIdFilter} ownerOptions={ownerOptions} {...formProps} />
      ),
    });
  };

  const renderActions = (progProd) => {
    if (!canEdit) return null;
    return (
      <>
        {progObj.tierBased && (
          <>
            <Button
              size="small"
              startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
              onClick={() => openTierDialog(EntityFormMode.ADD, progProd)}
            >
              Add tier
            </Button>
            <Button
              size="small"
              startIcon={<FontAwesomeIcon icon={fas.faClipboard} />}
              onClick={() => openTierBulkDialog(progProd)}
            >
              Paste tiers
            </Button>
          </>
        )}
        {canSaveProp && (
          <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
            onClick={() => openProgProdPropDialog(progProd)}
          >
            Add property
          </Button>
        )}
      </>
    );
  };

  const fetchProgProds = () => rsrc.rows('md', 'PROG_PROD', { expr: ExprUtils.whereParams('progId', progObj.id) });

  const openProgProdBulkDialog = () => {
    fetchProgProds().then((existingRows) => {
      openDialog(ProgProdBulkDialog, { progObj, existingRows: existingRows ?? [] }).then((result) => {
        const last = result?.results?.[result.results.length - 1];
        if (last?.id) setHighlightId(last.id);
      });
    });
  };

  // preloadExisting opens the grid as a set editor for the owner's current tiers instead of an empty paste target
  const openTierBulkDialog = (ownerProgProd, preloadExisting = false) => {
    fetchProgProds().then((progProds) => {
      openDialog(TierBulkDialog, {
        progObj,
        progProds: progProds ?? [],
        existingTiers: Object.values(progProdIdToTiers).flat(),
        ownerProgProd,
        preloadExisting,
      }).then((result) => {
        if (result?.ok && ownerProgProd) setHighlightId(ownerProgProd.id);
      });
    });
  };

  const headerButtons = canSaveProgProd && (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FontAwesomeIcon icon={fas.faClipboard} />}
        onClick={openProgProdBulkDialog}
      >
        Paste products
      </Button>
      {progObj.tierBased && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FontAwesomeIcon icon={fas.faClipboard} />}
          onClick={() => openTierBulkDialog()}
        >
          Paste tiers
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
        onClick={() => openProgProdDialog(EntityFormMode.ADD)}
      >
        Add product
      </Button>
    </>
  );

  const emptyState = (
    <Stack spacing={2} alignItems="center">
      <Typography color="text.secondary">
        {canEdit ? 'No products yet. Add products to this program.' : 'This program has no products.'}
      </Typography>
      {canSaveProgProd && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
            onClick={() => openProgProdDialog(EntityFormMode.ADD)}
          >
            Add product
          </Button>
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={fas.faClipboard} />}
            onClick={openProgProdBulkDialog}
          >
            Paste products from Excel
          </Button>
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      <EntityCardList
        module="md"
        entity="PROG_PROD"
        title="Products"
        augRowsReq={augRowsReq}
        refreshTrigger={tierRefreshTrigger}
        renderSummary={renderSummary}
        renderBody={renderBody}
        renderActions={renderActions}
        sortOptions={[
          {
            label: 'Product name',
            compare: (a, b) =>
              String(a.prodName ?? a.prodCode ?? '').localeCompare(String(b.prodName ?? b.prodCode ?? '')),
          },
          { label: 'Start date', compare: (a, b) => (a.startDate ?? 0) - (b.startDate ?? 0) },
          { label: 'End date', compare: (a, b) => (a.endDate ?? 0) - (b.endDate ?? 0) },
        ]}
        groupByOptions={
          showPricing ?
            [
              { label: 'Calc type', key: (row) => row.progProdCalcType },
              { label: basisLabel, key: (row) => row.pymtBasisCode },
            ]
          : []
        }
        contextMenuItems={(row) => contextMenuItems('PROG_PROD', row, canDeleteProgProd)}
        headerButtons={headerButtons}
        emptyState={emptyState}
        highlightId={highlightId}
      />
      {tierContextMenu}
    </>
  );
};

const Summary = ({ label, value, hidden = false, raw = false }) => {
  if (hidden) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {raw ?
        value
      : <Typography variant="body2" noWrap>
          {value ?? '—'}
        </Typography>
      }
    </Box>
  );
};
